/**
 * PostFormDialog — Erstellen/Bearbeiten eines Newsfeed-Beitrags
 * inkl. Sichtbarkeit, Optionen und Datei-Upload.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Pin, CheckCircle2, Loader2, Paperclip, FileIcon, X, Image, MessageSquare,
} from 'lucide-react';
import { VISIBILITY_OPTIONS, isImageFile } from './helpers';
import { FileDropzone } from '../../components/ui/file-dropzone';

export const PostFormDialog = ({
  open, onOpenChange,
  postForm, setPostForm, editingPost,
  categories, branches, locations, settings,
  uploadedFiles, uploading, onFileUpload, onRemoveFile,
  saving, onSave,
}) => {
  const setField = (field, value) => setPostForm({ ...postForm, [field]: value });

  const toggleId = (key, id, checked) => {
    const list = postForm[key];
    const ids = checked ? [...list, id] : list.filter((x) => x !== id);
    setPostForm({ ...postForm, [key]: ids });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editingPost ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Titel *</Label>
            <Input value={postForm.title} onChange={(e) => setField('title', e.target.value)}
              placeholder="Betreff / Überschrift" data-testid="input-post-title" />
          </div>
          <div className="space-y-2">
            <Label>Inhalt *</Label>
            <Textarea value={postForm.content} onChange={(e) => setField('content', e.target.value)}
              placeholder="Nachricht..." rows={6} data-testid="input-post-content" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select
                value={postForm.category_id || 'none'}
                onValueChange={(v) => setField('category_id', v === 'none' ? '' : v)}
              >
                <SelectTrigger data-testid="select-category">
                  <SelectValue placeholder="Keine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Kategorie</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <span style={{ color: cat.color }}>{cat.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sichtbarkeit</Label>
              <Select value={postForm.visibility_type} onValueChange={(v) => setField('visibility_type', v)}>
                <SelectTrigger data-testid="select-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {postForm.visibility_type === 'branch' && (
            <div className="space-y-2">
              <Label>Niederlassungen</Label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-muted/30 rounded-lg">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={postForm.visibility_branch_ids.includes(b.id)}
                      onCheckedChange={(c) => toggleId('visibility_branch_ids', b.id, c)}
                    />
                    {b.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {postForm.visibility_type === 'location' && (
            <div className="space-y-2">
              <Label>Standorte</Label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 bg-muted/30 rounded-lg">
                {locations.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={postForm.visibility_location_ids.includes(l.id)}
                      onCheckedChange={(c) => toggleId('visibility_location_ids', l.id, c)}
                    />
                    {l.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-4 pt-2">
            {settings.pinning_enabled && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={postForm.is_pinned}
                  onCheckedChange={(c) => setField('is_pinned', c)} />
                <Pin className="h-4 w-4 text-amber-500" /> Anpinnen
              </label>
            )}
            {settings.confirmations_enabled && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={postForm.require_confirmation}
                  onCheckedChange={(c) => setField('require_confirmation', c)} />
                <CheckCircle2 className="h-4 w-4 text-blue-500" /> Lesebestätigung
              </label>
            )}
            {settings.comments_enabled && (
              <label className="flex items-center gap-2 text-sm cursor-pointer" title="Wenn deaktiviert, sind für diesen Beitrag keine Kommentare möglich">
                <Checkbox checked={postForm.allow_comments !== false}
                  onCheckedChange={(c) => setField('allow_comments', c)}
                  data-testid="checkbox-allow-comments" />
                <MessageSquare className="h-4 w-4 text-emerald-600" /> Kommentare zulassen
              </label>
            )}
          </div>

          <div className="space-y-3 pt-2 border-t">
            <Label className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" /> Anhänge
            </Label>
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                {uploadedFiles.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-2 bg-muted/40 rounded-lg text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      {isImageFile(f.content_type)
                        ? <Image className="h-4 w-4 text-blue-500 shrink-0" />
                        : <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />}
                      <span className="truncate">{f.original_filename}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        ({Math.round(f.size / 1024)} KB)
                      </span>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                      onClick={() => onRemoveFile(f.id)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <FileDropzone
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                multiple
                onUpload={onFileUpload}
                uploading={uploading}
                hint="Bilder, PDF, Word, Excel, Text · max. 10 MB pro Datei · mehrere Dateien moeglich"
                testId="post-attachments-dropzone"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button onClick={onSave} disabled={saving} data-testid="btn-save-post">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingPost ? 'Aktualisieren' : 'Veröffentlichen'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
